import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type Inflation = {
  propertyid: number
  inflationid: number
}
// private module logic //

const id: string = "inflation"
const initialState: ApiResult<Inflation[]> = { id, state: "init", value: [] }
const inflationStream = new BehaviorSubject<ApiResult<Inflation[]>>(initialState)
const inflationEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let inflationMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function clear(): void {
  inflationStream.next({ id, state: 'ok', value: [] })
  inflationMap = {}
}

function get(inflationId: number): Inflation {
  return inflationMap[inflationId]
}

function getByPropertyId(propertyid: number): Inflation {
  return _.filter(inflationStream.getValue().value, {propertyid})
}

function refresh(): Promise<any> {
  const currentValue = inflationStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchInflation(propertyid)
}

function fetchInflation(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<Inflation[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  inflationStream.next({ id, state: 'busy', value: inflationStream.getValue().value })

  let url = `inflation?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `inflation?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: Inflation[]) => {
    _.forEach(records, (inflation) => (inflationMap[inflation.inflationid] = inflation))
    // merge results unique by inflationid
    inflationStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number, defaults: object): Promise<Inflation | { message: string }> {
  const currentValue = inflationStream.getValue().value
  inflationStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    compoundtype: defaults['inflationCompoundType'],
    inflationdetail: {
      state: 'basic',
      effectiveMonth: 'Market Effective Month'
    },
    inputtype: 'basic'
  }

  return Client.post(`inflation`, payload)
    .then((inflation: Inflation) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, inflation.inflationid),
        redofunc: () => create(propertyid, defaults)
      })
      inflationMap[inflation.inflationid] = inflation
      // merge results unique by inflationid
      const currentValue = inflationStream.getValue().value
      inflationStream.next({
        id,
        state: 'ok',
        value: _.unionBy([inflation], currentValue, 'inflationid')
      })
      inflationEventStream.next({ type: 'INFLATION_CREATED', payload: inflation })
      return inflation
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      inflationStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchInflation(propertyid)
      return err
    })
}

function copy(propertyid:number, inflationid:number, code:string) {
  const currentValue = inflationStream.getValue().value;
  inflationStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'inflation', recordid: inflationid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((inflation: Inflation) => {
      const newRecord = inflation[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.inflationid),
        redofunc: () => copy(propertyid, inflationid, code)
      });
      inflationMap[newRecord.inflationid] = newRecord
      // merge results unique by inflationid
      const currentValue = inflationStream.getValue().value
      inflationStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'inflationid').sort((a, b) => alphanum(a.code, b.code))
      })
      inflationEventStream.next({ type: 'INFLATION_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      inflationStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchInflation(propertyid)
      return err
    })
}

function copies(propertyid:number, inflationid:number, count: number) {
  const currentValue = inflationStream.getValue().value;
  inflationStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'inflation', recordid: inflationid, _synchronous: true, unique: true, count};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((inflations: Inflation[]) => {
      push_undo({
        undofunc: () => deleteMultiple(propertyid, _.map(inflations, 'inflationid')),
        redofunc: () => copies(propertyid, inflationid, count)
      });
      _.forEach(inflations, newRecord => inflationMap[newRecord.inflationid] = newRecord)
      // merge results unique by inflationid
      const currentValue = inflationStream.getValue().value
      inflationStream.next({
        id,
        state: 'ok',
        value: _.unionBy(inflations, currentValue, 'inflationid').sort((a, b) => alphanum(a.code, b.code))
      })
      inflationEventStream.next({ type: 'INFLATION_COPIED', payload: inflations })
      return inflations
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      inflationStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchInflation(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  inflationid: number,
): Promise<boolean | { message: string }> {
  const currentValue = inflationStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, inflationid)
  const refreshFn = () => fetchInflation(propertyid)
  if (!(await handleReferences('inflation', inflationid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted inflationid
  const value = _(currentValue)
    .filter((p) => p.inflationid !== inflationid)
    .value()
  value.forEach((p) => (inflationMap[p.inflationid] = p))

  inflationStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(inflationid).then(() =>
  Client.del(`inflation?inflationid=eq.${inflationid}`)
    .then((inflation: Inflation) => {
      delete inflationMap[inflation.inflationid]
      push_undo({
        undofunc: () => {
          Client.post('inflation', inflation)
          inflationMap[inflation.inflationid] = inflation
          inflationStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, inflationid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      inflationStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchInflation(propertyid)
      return err
    })
  )
}


async function deleteMultiple(
  propertyid: number,
  inflationids: number[],
): Promise<boolean | { message: string }> {
  const currentValue = inflationStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(propertyid, ids)
  const refreshFn = () => fetchInflation(propertyid)
  if (!(await handleReferences('inflation', inflationids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted inflationid
  const value = _(currentValue)
    .filter((p) => !inflationids.includes(p.inflationid))
    .value()
  value.forEach((p) => (inflationMap[p.inflationid] = p))

  inflationStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(inflationids).then(() =>
  Client.del(`inflation?inflationid=in.${inflationids}`)
    .then((inflations: Inflation[]) => {
      inflations.forEach(i => delete inflationMap[i.inflationid])
      push_undo({
        undofunc: () => {
          Client.post('inflation', inflations)
          inflations.forEach(i => inflationMap[i.inflationid] = i)
          inflationStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteMultiple(propertyid, inflationids)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      inflationStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchInflation(propertyid)
      return err
    })
  )
}

function patch(inflation: Inflation): Promise<Inflation | { message: string }> {
  const currentValue = inflationStream.getValue().value
  const inflationid: number = inflation.inflationid
  const currentInflation = _.find(currentValue, { inflationid })
  const changeset = diff_obj(inflation, currentInflation)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentInflation),
    redofunc: () => patch(inflation)
  })

  // optimistic update
  inflationStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {inflationid}), inflation)
  })

  return _patches_.watch(inflationid, Client.patch(`inflation?inflationid=eq.${inflationid}`, changeset)
    .then((mf: Inflation[]) => {
      _.forEach(mf, (inflation) => (inflationMap[inflation.inflationid] = inflation))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      inflationStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentInflation, 'propertyid')
      if (propertyid) fetchInflation(propertyid)
      return err
    }))
}

// public module interface //

const inflationService = {
  stream: () => inflationStream.asObservable(),
  value: () => inflationStream.getValue().value,
  eventStream: () => inflationEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  deleteMultiple,
  get,
  getByPropertyId,
  fetchInflation,
  patch,
  refresh
}

export default inflationService
